import { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Link } from "@inertiajs/react"
import { Check, LayoutList, Sparkles } from "lucide-react"
import { Layout } from "@/components/layouts/Layout"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { RunsChart } from "@/Pages/Agents/RunsChart"
import { useRunsFilters, RunsToolbar, Runs, COLUMNS } from "@/Pages/Agents/Runs"

// Insert the Agent column after Status, reuse all other columns from the shared COLUMNS definition
function buildColumns(workspace_slug) {
  const agentColumn = {
    accessorKey: "agent_name",
    header: "Agent",
    cell: (value, row) => (
      <Link
        href={`/${workspace_slug}/agents/${row.agent_id}`}
        onClick={(e) => e.stopPropagation()}
        className="truncate max-w-[160px] block"
        title={value}
      >
        {value || "Untitled agent"}
      </Link>
    ),
  }
  return [COLUMNS[0], agentColumn, ...COLUMNS.slice(1)]
}

export default function HistoryIndex({ workspace_slug, runs = [], chart_runs = [], pagination = {}, filters = {}, agents = [] }) {
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)

  // Agent filter state — ref updated synchronously so getExtraParams always reads the latest value
  const [agentIds, setAgentIds] = useState(filters.agents || [])
  const agentIdsRef = useRef(agentIds)
  agentIdsRef.current = agentIds

  const baseFilterProps = useRunsFilters({
    basePath: `/${workspace_slug}/history`,
    filters,
    pagination,
    getExtraParams: () => agentIdsRef.current.length > 0 ? { agents: agentIdsRef.current } : {},
  })

  const handleAgentToggle = (agentId) => {
    const next = agentIds.includes(agentId)
      ? agentIds.filter(id => id !== agentId)
      : [...agentIds, agentId]
    // Update ref synchronously before navigate fires so getExtraParams reads the new value
    agentIdsRef.current = next
    setAgentIds(next)
    baseFilterProps.navigate({ page: 1 })
  }

  const handleClearFilters = () => {
    agentIdsRef.current = []
    setAgentIds([])
    baseFilterProps.handleClearFilters()
  }

  const hasActiveFilters = baseFilterProps.hasActiveFilters || agentIds.length > 0

  const filterProps = { ...baseFilterProps, handleClearFilters, hasActiveFilters }

  const columns = buildColumns(workspace_slug)
  const showToolbar = hasActiveFilters || (pagination.count ?? 0) > 0

  const agentExtras = agents.length > 1 ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={`group/agent ${agentIds.length > 0 ? "border-slate-400 hover:border-slate-400" : ""}`}>
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground group-hover/agent:text-foreground transition-colors" />
          Agent
          {agentIds.length > 0 && (
            <Badge variant="secondary" className="ml-0.5 h-4 min-w-4 px-1 text-[10px] rounded-full">{agentIds.length}</Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {agents.map(agent => (
          <DropdownMenuItem
            key={agent.id}
            onSelect={(e) => { e.preventDefault(); handleAgentToggle(agent.id) }}
            className={`justify-between ${agentIds.includes(agent.id) ? "bg-accent" : ""}`}
          >
            <span className="truncate">{agent.name || "Untitled agent"}</span>
            {agentIds.includes(agent.id) && <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>
        <div className="flex items-center gap-1">
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">History</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col px-6 pb-6 gap-8">

          <h1 ref={nameRef} className="text-3xl font-medium">History</h1>

          <RunsChart runs={chart_runs} onBarClick={filterProps.handleBarDateRange} activeFrom={filterProps.from} />

          <div>
            <div className="flex items-end justify-between mb-3">
              <h2 className="text-md font-medium flex items-center gap-1.5">
                <LayoutList className="h-4 w-4 text-muted-foreground" />
                Runs
              </h2>
              {showToolbar && (
                <RunsToolbar {...filterProps} extras={agentExtras} />
              )}
            </div>
            <Runs
              runs={runs}
              pagination={pagination}
              filterProps={filterProps}
              columns={columns}
              emptyMessage="Runs will appear here once your agents start processing emails."
            />
          </div>

        </div>
      </ScrollArea>

    </Layout>
  )
}
